import { i18n } from "@lingui/core";
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import $switchCaseDefault from 'dolla-switch';
import { Divider } from '@prezi/bento';
import { selectDocument } from '../../../../store';
import { viewLinkActions } from '../../../../store/actions/viewLinkActions';
import * as Icons from '../../../atoms/icons';
import { Button, FooterContainer, InnerScreenGridShort, RowGap, RowGrid, SimpleSectionHeader } from '../../../atoms/dumb';
import { DisabledComponentWithTooltipWrapper, Header } from '../../../molecules/dumb';
import { fixViewLinkOwnPermissionWithLimit, isPermissionAllowed, isPermissionNotAllowedBecauseLimitReached, isPermissionNotAllowedInOrg } from '../../../../api/userPermissions';
import { createDataLookup, EIGHT } from '../../../const';
import { useConfig } from '../../../../utils/config';
import { DeactivationModal, LinkList, Placeholder } from '.';
import { localI18n } from '../../../../i18n';
import { getGlassboxLogger } from '../../../../glassboxLogger';
const uxCopy = {
  header: localI18n._("View links"),
  mainCTA: localI18n._("Create new link"),
  sectionHeaderOwner: localI18n._("Created by me"),
  sectionHeaderOther: localI18n._("Created by others")
};
const LinkListSection = styled.div.withConfig({
  displayName: "LinksOverview__LinkListSection",
  componentId: "sc-15celsi-0"
})(["padding:16px 20px;margin:0 -20px;"]);
const LinksHeaderWrapper = styled.div.withConfig({
  displayName: "LinksOverview__LinksHeaderWrapper",
  componentId: "sc-15celsi-1"
})(["display:flex;flex-direction:row;justify-content:space-between;align-items:center;width:100%;"]);
const ActionContainer = styled(FooterContainer).withConfig({
  displayName: "LinksOverview__ActionContainer",
  componentId: "sc-15celsi-2"
})(["display:flex;flex-direction:column;align-items:", ";padding-top:", "px;&&&{position:relative;left:0;right:0;}"], p => p.alignItemsCenter ? 'center' : 'flex-end', EIGHT * 3);
const getCreateTooltipUxCopy = reason => $switchCaseDefault(reason, {
  notAllowedClosedPrivacy: () => ({
    title: i18n._("Not allowed to create trackable links"),
    body: i18n._("Creating trackable links is not allowed for documents in \"Only individuals can access\"")
  }),
  notAllowedInOrg: () => ({
    title: i18n._("Not allowed to create trackable links"),
    body: i18n._("Creating trackable links is disabled for your team")
  }),
  notAllowedLimitReached: () => ({
    title: i18n._("Not allowed to create trackable links"),
    body: i18n._("Prezi Pro lets you assign separate links to track how different audiences interact with your presentation."),
    action: i18n._("Upgrade")
  })
}, () => {
  throw `Invalid component state: ${reason}`;
});
const createControlContextSelector = state => {
  var _state$userPermission;
  const totalLinkCount = state.viewlinks.value.allIds.length;
  const viewLinkOwnPermission = fixViewLinkOwnPermissionWithLimit(totalLinkCount, (_state$userPermission = state.userPermissions.value) === null || _state$userPermission === void 0 ? void 0 : _state$userPermission.viewLink.own);
  if (isPermissionNotAllowedInOrg(viewLinkOwnPermission === null || viewLinkOwnPermission === void 0 ? void 0 : viewLinkOwnPermission.create)) {
    return {
      disabled: true,
      reason: 'notAllowedInOrg'
    };
  }
  if (isPermissionNotAllowedBecauseLimitReached(viewLinkOwnPermission === null || viewLinkOwnPermission === void 0 ? void 0 : viewLinkOwnPermission.create)) {
    var _viewLinkOwnPermissio;
    return {
      disabled: true,
      paywall: {
        reason: 'notAllowedLimitReached',
        missingFeature: viewLinkOwnPermission === null || viewLinkOwnPermission === void 0 ? void 0 : (_viewLinkOwnPermissio = viewLinkOwnPermission.create.missing_features) === null || _viewLinkOwnPermissio === void 0 ? void 0 : _viewLinkOwnPermissio.features.at(0),
        logger: () => getGlassboxLogger().logLoadedPaywallCta({
          cta_paywall_type: 'SECOND_VIEW_LINK',
          paywall_cta_product: 'PRESENT'
        })
      }
    };
  }
  return {
    disabled: false
  };
};
const isGettingAllowed = userPermissions => {
  var _userPermissions$view, _userPermissions$view2;
  return isPermissionAllowed(userPermissions === null || userPermissions === void 0 ? void 0 : (_userPermissions$view = userPermissions.viewLink.own) === null || _userPermissions$view === void 0 ? void 0 : _userPermissions$view.get) || isPermissionAllowed(userPermissions === null || userPermissions === void 0 ? void 0 : (_userPermissions$view2 = userPermissions.viewLink.all) === null || _userPermissions$view2 === void 0 ? void 0 : _userPermissions$view2.get);
};
const isUpdatingAllowed = userPermissions => {
  var _userPermissions$view3, _userPermissions$view4;
  return isPermissionAllowed(userPermissions === null || userPermissions === void 0 ? void 0 : (_userPermissions$view3 = userPermissions.viewLink.own) === null || _userPermissions$view3 === void 0 ? void 0 : _userPermissions$view3.update) || isPermissionAllowed(userPermissions === null || userPermissions === void 0 ? void 0 : (_userPermissions$view4 = userPermissions.viewLink.all) === null || _userPermissions$view4 === void 0 ? void 0 : _userPermissions$view4.update);
};
const CreateNewLinkButton = ({
  controlContext,
  onCreateNewLink
}) => {
  if (controlContext.disabled) {
    const {
      onUpgrade
    } = useConfig();
    return React.createElement(DisabledComponentWithTooltipWrapper, {
      uxCopyTooltip: controlContext.paywall ? getCreateTooltipUxCopy(controlContext.paywall.reason) : getCreateTooltipUxCopy(controlContext.reason),
      onActionTooltip: controlContext.paywall && (() => onUpgrade('pitch_analytics')),
      paywall: controlContext.paywall
    }, React.createElement(Button, {
      disabled: controlContext.disabled,
      variant: "primary",
      "data-lookup": createDataLookup('create-new-view-link-button')
    }, React.createElement(Icons.Plus, null), React.createElement("span", null, uxCopy.mainCTA)));
  }
  return React.createElement(Button, {
    onClick: onCreateNewLink,
    variant: "primary",
    "data-lookup": createDataLookup('create-new-view-link-button')
  }, React.createElement(Icons.Plus, null), React.createElement("span", null, uxCopy.mainCTA));
};
const LinksOverview = ({
  onEdit,
  onBack,
  onCreateNewLink
}) => {
  var _useSelector$value;
  const dispatch = useDispatch();
  const createControlContext = useSelector(createControlContextSelector);
  const userPermissions = useSelector(state => ({
    isLoading: state.userPermissions.loading,
    gettingAllowed: isGettingAllowed(state.userPermissions.value),
    updatingAllowed: isUpdatingAllowed(state.userPermissions.value)
  }));
  const viewLinks = useSelector(state => ({
    isLoading: state.viewlinks.loading,
    list: state.viewlinks.value.asList()
  }));
  const loading = viewLinks.isLoading || userPermissions.isLoading;
  const hasList = userPermissions.gettingAllowed && (viewLinks.list.length || loading);
  const ownerLinksList = viewLinks.list.filter(link => link.is_owner);
  const someOwner = ownerLinksList.length > 0;
  const otherLinksList = viewLinks.list.filter(link => !link.is_owner);
  const someOther = otherLinksList.length > 0;
  const [linkIdToDeactivate, setLinkIdToDeactivate] = React.useState(null);
  const documentSchemaVersionName = (_useSelector$value = useSelector(selectDocument).value) === null || _useSelector$value === void 0 ? void 0 : _useSelector$value.schema_version_name;
  if (!hasList) {
    return React.createElement(InnerScreenGridShort, {
      rowGap: RowGap.none
    }, React.createElement(LinksHeaderWrapper, null, React.createElement(Header, {
      onBack: onBack,
      removeBottomBorder: true
    }, uxCopy.header)), React.createElement(Placeholder, null), React.createElement(ActionContainer, {
      alignItemsCenter: true
    }, React.createElement(CreateNewLinkButton, {
      controlContext: createControlContext,
      onCreateNewLink: onCreateNewLink
    })));
  }
  return React.createElement(InnerScreenGridShort, {
    rowGap: RowGap.none
  }, React.createElement(LinksHeaderWrapper, null, React.createElement(Header, {
    onBack: onBack,
    removeBottomBorder: true
  }, uxCopy.header), !loading && React.createElement(CreateNewLinkButton, {
    controlContext: createControlContext,
    onCreateNewLink: onCreateNewLink
  })), React.createElement(LinkListSection, null, React.createElement(RowGrid, {
    rowGap: RowGap.medium
  }, someOwner && React.createElement(React.Fragment, null, React.createElement(SimpleSectionHeader, null, uxCopy.sectionHeaderOwner), React.createElement(LinkList, {
    links: ownerLinksList,
    documentSchemaVersionName: documentSchemaVersionName,
    handleOnClick: onEdit,
    disabled: !userPermissions.updatingAllowed
  })), someOwner && someOther && React.createElement(Divider, null), someOther && React.createElement(React.Fragment, null, React.createElement(SimpleSectionHeader, null, uxCopy.sectionHeaderOther), React.createElement(LinkList, {
    links: otherLinksList,
    documentSchemaVersionName: documentSchemaVersionName,
    handleOnClick: setLinkIdToDeactivate,
    disabled: !userPermissions.updatingAllowed
  }), linkIdToDeactivate && React.createElement(DeactivationModal, {
    isOpen: Boolean(linkIdToDeactivate),
    onClose: () => setLinkIdToDeactivate(null),
    handleDeactivateLink: deleteData => {
      setLinkIdToDeactivate(null);
      if (onBack) {
        onBack();
      }
      dispatch(viewLinkActions.removeViewlink(linkIdToDeactivate, deleteData));
    }
  })))));
};
export default LinksOverview;